import React, { useRef, useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import Text from "components/Text";
import Input from "components/Input";
import Loader from "components/Loader";
import Empty from "components/Empty";
import crossIcon from "../../../../assets/images/cross.svg";
import iconTick from "assets/images/icon_tick.svg";
import editIcon from "assets/images/edit-icon.svg";
import deleteIcon from "assets/images/delete-icon.svg";
import plusIcon from "assets/images/plus-icon-outline.svg";
import {
  addPolicyCategory,
  editPolicyCategory,
  deletePolicyCategory,
} from "repositories/office-policy-management-repository";
import toast from "react-hot-toast";
import styles from "../OfficePolicyManagement.module.scss";

const MAX_CATEGORY_NAME_LENGTH = 100;

const SelectCategoryModal = ({
  t,
  isOpen,
  closeModal,
  categories = [],
  selectedCategory,
  onSelectCategory,
  onApply,
  accountOwnerId,
  refetchCategories,
}) => {
  const [selectedVal, setSelectedVal] = useState(selectedCategory || null);
  const [error, setError] = useState("");
  const [addingLoader, setAddingLoader] = useState(false);
  const [isAddingInputEnabled, setIsAddingInputEnabled] = useState(false);
  const categoryNameInputRef = useRef("");
  const categoryNameEditRef = useRef(undefined);

  useEffect(() => {
    if (isOpen) {
      setSelectedVal(selectedCategory || null);
    }
  }, [isOpen, selectedCategory]);

  const categoryList = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return [...list]
      .filter((c) => !c?.isDeleted)
      .sort((a, b) => {
        const nameA = (a?.title || "").toLowerCase();
        const nameB = (b?.title || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [categories]);

  const getCategoryName = (c) => c?.title ?? "";

  const deleteCategoryHandler = async (id) => {
    const category = categoryList.find((c) => c.id === id);
    if (!category?.isDeletable) return;
    const isAssigned =
      category?.isAssigned ?? category?.isAssignedToPolicy ?? false;
    if (isAssigned) {
      toast.error(t("officePolicyManagement.categoryAssignedCannotDelete"));
      return;
    }
    setAddingLoader(true);
    try {
      await deletePolicyCategory(id);
      toast.success(t("officePolicyManagement.categoryDeleted"));
      refetchCategories?.();
      if (selectedVal?.id === id) {
        setSelectedVal(null);
      }
      if (isAddingInputEnabled) {
        setIsAddingInputEnabled(false);
      }
    } catch (err) {
      toast.error(
        err?.message || t("officePolicyManagement.deleteCategoryFailed"),
      );
    } finally {
      setAddingLoader(false);
    }
  };

  const addEditCategoryHandler = async () => {
    const value = (categoryNameInputRef.current || "").trim();
    if (!value) {
      return setError(t("officePolicyManagement.pleaseAddCategoryName"));
    }
    if (value.length > MAX_CATEGORY_NAME_LENGTH) {
      return setError(t("officePolicyManagement.categoryNameMaxLength"));
    }
    setAddingLoader(true);
    setError("");
    try {
      const editValueSelected = categoryNameEditRef.current;
      if (editValueSelected) {
        if (value === getCategoryName(editValueSelected)) {
          setIsAddingInputEnabled(false);
          categoryNameEditRef.current = undefined;
          categoryNameInputRef.current = "";
          setAddingLoader(false);
          return;
        }
        await editPolicyCategory({
          id: editValueSelected.id,
          OwnerId: accountOwnerId,
          Title: value,
        });
        toast.success(t("officePolicyManagement.categoryUpdated"));
        categoryNameEditRef.current = undefined;
        if (selectedVal?.id === editValueSelected.id) {
          setSelectedVal((prev) => (prev ? { ...prev, Title: value } : null));
        }
      } else {
        await addPolicyCategory({ OwnerId: accountOwnerId, Title: value });
        toast.success(t("officePolicyManagement.categoryAdded"));
      }
      categoryNameInputRef.current = "";
      refetchCategories?.();
      setIsAddingInputEnabled(false);
    } catch (err) {
      toast.error(
        err?.message || t("officePolicyManagement.addCategoryFailed"),
      );
    } finally {
      setAddingLoader(false);
    }
  };

  const handleApply = () => {
    onSelectCategory(selectedVal);
    onApply(selectedVal);
    closeModal();
  };

  const handleClose = () => {
    setSelectedVal(selectedCategory || null);
    setIsAddingInputEnabled(false);
    categoryNameEditRef.current = undefined;
    categoryNameInputRef.current = "";
    setError("");
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      className="modal-dialog-centered modal-width-660"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={handleClose}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody>
        {addingLoader && <Loader />}
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {t("officePolicyManagement.selectCategory")}
          </span>
        </Text>
        {isAddingInputEnabled ? (
          <Input
            Autofocus
            key={categoryNameEditRef.current ? "edit" : "add"}
            Title={
              categoryNameEditRef.current
                ? t("officePolicyManagement.editCategory")
                : t("officePolicyManagement.addCategory")
            }
            Type="text"
            Placeholder={t("officePolicyManagement.categoryNamePlaceholder")}
            MaxLength={MAX_CATEGORY_NAME_LENGTH}
            Name="categoryName"
            HandleChange={(e) => {
              const val = e.target.value.trim();
              if (val && error) setError("");
              categoryNameInputRef.current = val;
            }}
            Error={error}
            HelperLabel={
              <span
                className="link-btn d-flex align-items-center h-100 my-2 bg-white"
                onClick={addEditCategoryHandler}
              >
                <img
                  src={iconTick}
                  alt="icon"
                  width={15}
                  style={{ marginRight: 3 }}
                />
                {categoryNameEditRef.current
                  ? t("officePolicyManagement.edit")
                  : t("officePolicyManagement.add")}
              </span>
            }
            defaultValue={
              categoryNameEditRef.current &&
              getCategoryName(categoryNameEditRef.current)
            }
          />
        ) : (
          <span
            className="link-btn"
            onClick={() => setIsAddingInputEnabled(true)}
          >
            <img src={plusIcon} alt="icon" width={20} className="me-xxs" />
            {t("officePolicyManagement.addCategory")}
          </span>
        )}
        {categoryList.length > 0 ? (
          <ul
            className={
              styles["change-list"] + " my-2 " + styles["modal-view-lists"]
            }
          >
            {categoryList?.length > 0 &&
              categoryList?.map((item) => {
                const isAssigned =
                  item?.isAssigned ?? item?.isAssignedToPolicy ?? false;
                return (
                  <li key={item.id}>
                    <div className="ch-radio d-flex justify-content-between">
                      <label className={styles.label_width}>
                        <input
                          type="radio"
                          readOnly
                          onClick={() => setSelectedVal(item)}
                          checked={selectedVal?.id === item.id}
                          name="selectedCategory"
                        />
                        <span className="word-break">
                          {getCategoryName(item)}
                        </span>
                      </label>
                      <div className="d-flex gap-sm">
                        <img
                          src={editIcon}
                          alt="edit_icon"
                          width={20}
                          onClick={() => {
                            setIsAddingInputEnabled(false);
                            categoryNameInputRef.current =
                              getCategoryName(item);
                            categoryNameEditRef.current = item;
                            setTimeout(
                              () => setIsAddingInputEnabled(true),
                              200,
                            );
                          }}
                        />
                        {item?.isDeletable && (
                          <img
                            src={deleteIcon}
                            alt="delete_icon"
                            width={20}
                            style={isAssigned ? { cursor: "not-allowed" } : {}}
                            onClick={() => {
                              if (isAssigned) {
                                toast.error(
                                  t(
                                    "officePolicyManagement.categoryAssignedCannotDelete",
                                  ),
                                );
                              } else {
                                deleteCategoryHandler(item.id);
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        ) : (
          <Empty
            Message={t("officePolicyManagement.noDataAvailableForSelection")}
          />
        )}
        <button
          className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
          title={t("apply")}
          onClick={handleApply}
        >
          {t("officePolicyManagement.apply")}
        </button>
        <button
          className="button button-round button-border button-dark btn-mobile-link"
          onClick={handleClose}
          title={t("cancel")}
        >
          {t("officePolicyManagement.cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
};

export default withTranslation()(SelectCategoryModal);
